/**
 * Sentinel Response - Monitoring Dashboard
 * Real-time telemetry, alerts, remote commands, and command history with results.
 *
 * Phase 4 fixes:
 *  - Commands auto-poll every 3s after dispatch until completed
 *  - Selected asset persisted in URL query param (survives refresh)
 *  - Collect logs output rendered as formatted table
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { monitoringApi } from '../api/client';
import { useSearchParams } from 'react-router-dom';
import {
  Activity, Wifi, WifiOff, AlertTriangle, Cpu,
  HardDrive, MemoryStick, Clock, Terminal,
  ChevronRight, ChevronDown, CheckCircle2, XCircle,
  ArrowLeft, Loader2,
} from 'lucide-react';
import {
  XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area,
} from 'recharts';
import { formatDistanceToNow, format } from 'date-fns';

const REFRESH_INTERVAL = 30000;
const COMMAND_POLL_FAST = 3000;

function StatCard({ icon: Icon, label, value, color = 'gray', sub }) {
  const colors = {
    emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    red: 'bg-red-500/10 text-red-400 border-red-500/20',
    amber: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    blue: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    gray: 'bg-gray-800/70 text-gray-300 border-gray-700/60',
  };
  return (
    <div className={`rounded-xl border p-5 ${colors[color]}`}>
      <div className="flex items-start justify-between mb-3"><Icon className="w-5 h-5 opacity-60" /></div>
      <div className="text-3xl font-semibold tracking-tight">{value}</div>
      <div className="text-sm mt-1 opacity-70">{label}</div>
      {sub && <div className="text-xs mt-2 opacity-50">{sub}</div>}
    </div>
  );
}

function MiniGauge({ value, label }) {
  const pct = Math.min(100, value || 0);
  const color = pct >= 90 ? '#ef4444' : pct >= 75 ? '#f59e0b' : '#10b981';
  return (
    <div className="w-20">
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-xs text-gray-500">{label}</span>
        <span className="text-xs font-medium tabular-nums" style={{ color }}>{value != null ? `${Math.round(value)}%` : '--'}</span>
      </div>
      <div className="h-1.5 bg-gray-700/70 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

function DiskBar({ drive, percent, free_gb }) {
  const color = percent >= 90 ? '#ef4444' : percent >= 80 ? '#f59e0b' : '#10b981';
  return (
    <div className="flex items-center gap-3">
      <HardDrive className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
      <span className="text-xs text-gray-400 w-8">{drive}</span>
      <div className="flex-1 h-1.5 bg-gray-700/70 rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${percent}%`, backgroundColor: color }} />
      </div>
      <span className="text-xs tabular-nums text-gray-500 w-16 text-right">{free_gb} GB free</span>
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 shadow-xl text-xs">
      <p className="text-gray-400 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-medium">{p.name}: {p.value != null ? `${Math.round(p.value)}%` : '--'}</p>
      ))}
    </div>
  );
}

const CMD_STATUS_STYLES = {
  pending: { bg: 'bg-blue-500/15', text: 'text-blue-400', icon: Clock },
  sent: { bg: 'bg-blue-500/15', text: 'text-blue-400', icon: Clock },
  running: { bg: 'bg-amber-500/15', text: 'text-amber-400', icon: Loader2 },
  completed: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', icon: CheckCircle2 },
  failed: { bg: 'bg-red-500/15', text: 'text-red-400', icon: XCircle },
  cancelled: { bg: 'bg-gray-500/15', text: 'text-gray-400', icon: XCircle },
  expired: { bg: 'bg-gray-500/15', text: 'text-gray-500', icon: Clock },
};

function LogTable({ stdout }) {
  try {
    const logs = JSON.parse(stdout);
    if (!Array.isArray(logs) || logs.length === 0) return null;
    const levelColor = (lvl) => {
      const l = (lvl || '').toLowerCase();
      if (l === 'critical') return 'bg-red-500/20 text-red-400';
      if (l === 'error') return 'bg-red-500/15 text-red-400';
      if (l === 'warning') return 'bg-amber-500/15 text-amber-400';
      if (l === 'information' || l === 'info') return 'bg-blue-500/10 text-blue-400';
      return 'bg-gray-500/15 text-gray-400';
    };
    return (
      <div>
        <div className="text-[10px] uppercase tracking-wider text-gray-600 mb-1">Event logs ({logs.length} entries)</div>
        <div className="bg-gray-900/80 rounded-lg overflow-hidden max-h-72 overflow-y-auto">
          <table className="w-full text-xs">
            <thead><tr className="border-b border-gray-700/50 sticky top-0 bg-gray-900">
              <th className="text-left p-2 text-gray-500 font-medium w-20">Level</th>
              <th className="text-left p-2 text-gray-500 font-medium w-32">Source</th>
              <th className="text-left p-2 text-gray-500 font-medium w-14">ID</th>
              <th className="text-left p-2 text-gray-500 font-medium">Message</th>
              <th className="text-left p-2 text-gray-500 font-medium w-36">Time</th>
            </tr></thead>
            <tbody>{logs.slice(0, 100).map((log, i) => (
              <tr key={i} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                <td className="p-2"><span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${levelColor(log.level || log.Level)}`}>{log.level || log.Level || '?'}</span></td>
                <td className="p-2 text-gray-300 truncate max-w-[8rem]">{log.source || log.Source || log.ProviderName || '--'}</td>
                <td className="p-2 text-gray-400 tabular-nums">{log.id || log.Id || log.event_id || log.EventID || '--'}</td>
                <td className="p-2 text-gray-300 truncate max-w-xs">{log.message || log.Message || '--'}</td>
                <td className="p-2 text-gray-500 whitespace-nowrap text-[11px]">{(log.time || log.TimeCreated || log.Time) ? format(new Date(log.time || log.TimeCreated || log.Time), 'MMM d, h:mm a') : '--'}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </div>
    );
  } catch { return null; }
}

function isJsonLogArray(str) {
  try { return Array.isArray(JSON.parse(str)); } catch { return false; }
}

function CommandHistoryPanel({ assetId, refreshTrigger }) {
  const [commands, setCommands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const fastPollRef = useRef(null);

  const loadCommands = useCallback(async () => {
    try {
      const res = await monitoringApi.getCommands({ asset_id: assetId });
      const cmds = res.data;
      setCommands(cmds);
      const hasInFlight = cmds.some(c => ['pending', 'sent', 'running'].includes(c.status));
      if (hasInFlight && !fastPollRef.current) {
        fastPollRef.current = setInterval(async () => {
          try {
            const r = await monitoringApi.getCommands({ asset_id: assetId });
            setCommands(r.data);
            const stillInFlight = r.data.some(c => ['pending', 'sent', 'running'].includes(c.status));
            if (!stillInFlight && fastPollRef.current) {
              clearInterval(fastPollRef.current);
              fastPollRef.current = null;
              const justDone = r.data.find(c => c.status === 'completed' || c.status === 'failed');
              if (justDone) setExpandedId(justDone.id);
            }
          } catch {}
        }, COMMAND_POLL_FAST);
      }
    } catch (err) {
      console.error('Failed to load commands:', err);
    } finally {
      setLoading(false);
    }
  }, [assetId]);

  useEffect(() => {
    loadCommands();
    const interval = setInterval(loadCommands, REFRESH_INTERVAL);
    return () => { clearInterval(interval); if (fastPollRef.current) { clearInterval(fastPollRef.current); fastPollRef.current = null; } };
  }, [loadCommands]);

  useEffect(() => { if (refreshTrigger > 0) loadCommands(); }, [refreshTrigger, loadCommands]);

  if (loading) return <div className="bg-gray-800/40 border border-gray-700/50 rounded-xl p-5"><div className="animate-pulse h-20 bg-gray-800 rounded" /></div>;

  return (
    <div className="bg-gray-800/40 border border-gray-700/50 rounded-xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <Terminal className="w-4 h-4 text-gray-400" />
        <h3 className="text-sm font-medium text-gray-400">Command history</h3>
        <span className="text-xs text-gray-600 ml-auto">{commands.length} command{commands.length !== 1 ? 's' : ''}</span>
      </div>
      {commands.length === 0 ? (
        <div className="text-sm text-gray-600 py-4 text-center">No commands have been executed yet.</div>
      ) : (
        <div className="space-y-2">
          {commands.map((cmd) => {
            const style = CMD_STATUS_STYLES[cmd.status] || CMD_STATUS_STYLES.pending;
            const StatusIcon = style.icon;
            const isExpanded = expandedId === cmd.id;
            return (
              <div key={cmd.id} className="rounded-lg border border-gray-700/40 overflow-hidden">
                <button onClick={() => setExpandedId(isExpanded ? null : cmd.id)}
                  className="w-full flex items-center gap-3 p-3 hover:bg-gray-700/20 transition-colors text-left">
                  <StatusIcon className={`w-4 h-4 flex-shrink-0 ${style.text} ${cmd.status === 'running' ? 'animate-spin' : ''}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-200 font-medium">{cmd.command_type.replace(/_/g, ' ')}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider ${style.bg} ${style.text}`}>{cmd.status}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {cmd.created_at ? formatDistanceToNow(new Date(cmd.created_at), { addSuffix: true }) : ''}
                      {cmd.completed_at && cmd.created_at ? ` · took ${Math.round((new Date(cmd.completed_at) - new Date(cmd.created_at)) / 1000)}s` : ''}
                    </div>
                  </div>
                  {cmd.exit_code != null && <span className={`text-xs tabular-nums ${cmd.exit_code === 0 ? 'text-emerald-400' : 'text-red-400'}`}>exit {cmd.exit_code}</span>}
                  <ChevronDown className={`w-4 h-4 text-gray-600 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </button>
                {isExpanded && (
                  <div className="border-t border-gray-700/40 p-3 space-y-3">
                    {cmd.parameters && Object.keys(cmd.parameters).length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-gray-600 mb-1">Parameters</div>
                        <div className="bg-gray-900/60 rounded-lg p-2.5">
                          {Object.entries(cmd.parameters).map(([key, val]) => (
                            <div key={key} className="flex items-center gap-2 text-xs">
                              <span className="text-gray-500">{key}:</span>
                              <span className="text-gray-300 font-mono">{typeof val === 'object' ? JSON.stringify(val) : String(val)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {cmd.result_summary && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-gray-600 mb-1">Result</div>
                        <div className="bg-gray-900/60 rounded-lg p-2.5 text-xs text-gray-300">{cmd.result_summary}</div>
                      </div>
                    )}
                    {cmd.error_message && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-red-500/70 mb-1">Error</div>
                        <div className="bg-red-500/5 border border-red-500/10 rounded-lg p-2.5 text-xs text-red-400 font-mono whitespace-pre-wrap">{cmd.error_message}</div>
                      </div>
                    )}
                    {cmd.command_type === 'collect_logs' && cmd.result_data?.stdout && <LogTable stdout={cmd.result_data.stdout} />}
                    {cmd.result_data && (cmd.result_data.stdout || cmd.result_data.stderr) && !(cmd.command_type === 'collect_logs' && cmd.result_data.stdout && isJsonLogArray(cmd.result_data.stdout)) && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-gray-600 mb-1">Output</div>
                        <div className="bg-gray-900/80 rounded-lg p-3 max-h-64 overflow-y-auto">
                          {cmd.result_data.stdout && <pre className="text-xs text-gray-300 font-mono whitespace-pre-wrap break-words">{cmd.result_data.stdout}</pre>}
                          {cmd.result_data.stderr && <pre className="text-xs text-amber-400/80 font-mono whitespace-pre-wrap break-words mt-2">{cmd.result_data.stderr}</pre>}
                        </div>
                      </div>
                    )}
                    {cmd.command_type === 'check_health' && cmd.result_data && cmd.result_data.cpu_percent != null && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-gray-600 mb-1">Health snapshot</div>
                        <div className="grid grid-cols-3 gap-2">
                          <div className="bg-gray-900/60 rounded-lg p-2.5 text-center"><div className="text-xs text-gray-500">CPU</div><div className="text-sm font-medium text-gray-200">{Math.round(cmd.result_data.cpu_percent)}%</div></div>
                          <div className="bg-gray-900/60 rounded-lg p-2.5 text-center"><div className="text-xs text-gray-500">Memory</div><div className="text-sm font-medium text-gray-200">{Math.round(cmd.result_data.memory_percent)}%</div></div>
                          <div className="bg-gray-900/60 rounded-lg p-2.5 text-center"><div className="text-xs text-gray-500">Uptime</div><div className="text-sm font-medium text-gray-200">{cmd.result_data.uptime_seconds ? `${Math.floor(cmd.result_data.uptime_seconds / 86400)}d` : '--'}</div></div>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-4 text-[10px] text-gray-600 pt-1">
                      {cmd.created_at && <span>Queued: {format(new Date(cmd.created_at), 'MMM d, h:mm:ss a')}</span>}
                      {cmd.completed_at && <span>Completed: {format(new Date(cmd.completed_at), 'MMM d, h:mm:ss a')}</span>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AssetDetail({ asset, onBack, onCommand, isSentinelUser }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cmdRefresh, setCmdRefresh] = useState(0);

  useEffect(() => {
    async function load() {
      try {
        const res = await monitoringApi.getAssetTelemetry(asset.id, 60);
        setHistory(res.data.map(d => ({ ...d, time: d.collected_at ? format(new Date(d.collected_at), 'HH:mm') : '' })));
      } catch (err) { console.error('Failed to load telemetry history:', err); }
      finally { setLoading(false); }
    }
    load();
    const interval = setInterval(load, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [asset.id]);

  function handleCommand(assetId, commandType) {
    onCommand(assetId, commandType);
    setCmdRefresh(prev => prev + 1);
  }

  const t = asset.telemetry;
  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-300 mb-4 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to overview
      </button>
      <div className="flex items-center gap-3 mb-6">
        <div className={`w-3 h-3 rounded-full ${asset.status === 'online' ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}`} />
        <h2 className="text-xl font-semibold text-white">{asset.name}</h2>
        <span className="text-sm text-gray-500">{asset.hostname}</span>
        <span className="text-xs text-gray-600 px-2 py-0.5 bg-gray-800 rounded">{asset.criticality}</span>
      </div>
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-800/40 border border-gray-700/50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2"><Cpu className="w-4 h-4 text-gray-500" /><span className="text-xs text-gray-500">CPU</span></div>
          <div className="text-2xl font-bold tabular-nums" style={{ color: (t?.cpu_percent || 0) >= 90 ? '#ef4444' : (t?.cpu_percent || 0) >= 75 ? '#f59e0b' : '#10b981' }}>
            {t?.cpu_percent != null ? `${Math.round(t.cpu_percent)}%` : '--'}
          </div>
        </div>
        <div className="bg-gray-800/40 border border-gray-700/50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2"><MemoryStick className="w-4 h-4 text-gray-500" /><span className="text-xs text-gray-500">Memory</span></div>
          <div className="text-2xl font-bold tabular-nums" style={{ color: (t?.memory_percent || 0) >= 90 ? '#ef4444' : (t?.memory_percent || 0) >= 75 ? '#f59e0b' : '#10b981' }}>
            {t?.memory_percent != null ? `${Math.round(t.memory_percent)}%` : '--'}
          </div>
          <div className="text-xs text-gray-500 mt-1">{t?.memory_used_gb ?? '--'} / {t?.memory_total_gb ?? '--'} GB</div>
        </div>
        <div className="bg-gray-800/40 border border-gray-700/50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2"><Clock className="w-4 h-4 text-gray-500" /><span className="text-xs text-gray-500">Uptime</span></div>
          <div className="text-2xl font-bold tabular-nums text-gray-200">{t?.uptime_seconds ? `${Math.floor(t.uptime_seconds / 86400)}d` : '--'}</div>
          <div className="text-xs text-gray-500 mt-1">{t?.uptime_seconds ? `${Math.floor((t.uptime_seconds % 86400) / 3600)}h ${Math.floor((t.uptime_seconds % 3600) / 60)}m` : ''}</div>
        </div>
        <div className="bg-gray-800/40 border border-gray-700/50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2"><Activity className="w-4 h-4 text-gray-500" /><span className="text-xs text-gray-500">Agent</span></div>
          <div className="text-lg font-medium text-gray-200 mt-1">v{asset.agent_version || '?'}</div>
          <div className="text-xs text-gray-500 mt-1">{asset.last_seen_at ? formatDistanceToNow(new Date(asset.last_seen_at), { addSuffix: true }) : 'Never'}</div>
        </div>
      </div>
      <div className="bg-gray-800/40 border border-gray-700/50 rounded-xl p-5 mb-6">
        <h3 className="text-sm font-medium text-gray-400 mb-4">CPU & memory history</h3>
        {history.length > 1 ? (
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={history}>
              <defs>
                <linearGradient id="cpuGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} /><stop offset="95%" stopColor="#3b82f6" stopOpacity={0} /></linearGradient>
                <linearGradient id="memGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10b981" stopOpacity={0.3} /><stop offset="95%" stopColor="#10b981" stopOpacity={0} /></linearGradient>
              </defs>
              <XAxis dataKey="time" tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 100]} tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false} width={35} />
              <Tooltip content={<ChartTooltip />} />
              <Area type="monotone" dataKey="cpu_percent" name="CPU" stroke="#3b82f6" fill="url(#cpuGrad)" strokeWidth={1.5} dot={false} />
              <Area type="monotone" dataKey="memory_percent" name="Memory" stroke="#10b981" fill="url(#memGrad)" strokeWidth={1.5} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[200px] flex items-center justify-center text-sm text-gray-600">{loading ? 'Loading history...' : 'Waiting for telemetry data to accumulate...'}</div>
        )}
        <div className="flex items-center gap-4 mt-2 justify-center">
          <span className="flex items-center gap-1.5 text-xs text-gray-400"><span className="w-3 h-0.5 bg-blue-500 rounded" /> CPU</span>
          <span className="flex items-center gap-1.5 text-xs text-gray-400"><span className="w-3 h-0.5 bg-emerald-500 rounded" /> Memory</span>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-gray-800/40 border border-gray-700/50 rounded-xl p-5">
          <h3 className="text-sm font-medium text-gray-400 mb-3">Disk usage</h3>
          <div className="space-y-3">
            {(t?.disk_usage || []).map((d, i) => <DiskBar key={i} drive={d.drive || d.mountpoint} percent={d.percent} free_gb={d.free_gb} />)}
            {(!t?.disk_usage || t.disk_usage.length === 0) && <span className="text-sm text-gray-600">No disk data</span>}
          </div>
        </div>
        <div className="bg-gray-800/40 border border-gray-700/50 rounded-xl p-5">
          <h3 className="text-sm font-medium text-gray-400 mb-3">Services</h3>
          <div className="space-y-2">
            {(t?.services || []).slice(0, 8).map((svc, i) => (
              <div key={i} className="flex items-center justify-between">
                <span className="text-sm text-gray-300 truncate">{svc.display_name || svc.name}</span>
                <span className={`text-xs px-2 py-0.5 rounded ${svc.status === 'running' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>{svc.status}</span>
              </div>
            ))}
            {(!t?.services || t.services.length === 0) && <span className="text-sm text-gray-600">No service data</span>}
          </div>
        </div>
      </div>
      {isSentinelUser && (
        <div className="bg-gray-800/40 border border-gray-700/50 rounded-xl p-5 mb-6">
          <div className="flex items-center gap-2 mb-4"><Terminal className="w-4 h-4 text-gray-400" /><h3 className="text-sm font-medium text-gray-400">Remote actions</h3></div>
          <div className="flex flex-wrap gap-2">
            {[
              { type: 'check_health', label: 'Health check', danger: false },
              { type: 'collect_logs', label: 'Collect logs', danger: false },
              { type: 'restart_service', label: 'Restart service', danger: true },
              { type: 'install_updates', label: 'Install updates', danger: true },
            ].map(({ type, label, danger }) => (
              <button key={type} onClick={() => handleCommand(asset.id, type)}
                className={`px-3 py-2 text-sm rounded-lg transition-colors ${danger ? 'bg-amber-600/15 text-amber-400 hover:bg-amber-600/25' : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700'}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
      )}
      <CommandHistoryPanel assetId={asset.id} refreshTrigger={cmdRefresh} />
    </div>
  );
}

export default function MonitoringPage() {
  const { tenant, isSentinelUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [overview, setOverview] = useState(null);
  const [assets, setAssets] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commandSent, setCommandSent] = useState(null);

  const selectedAsset = searchParams.get('asset');
  const setSelectedAsset = (id) => { if (id) { setSearchParams({ asset: id }); } else { setSearchParams({}); } };

  const tenantId = isSentinelUser ? undefined : tenant?.id;

  const loadData = useCallback(async () => {
    try {
      const [o, a, al] = await Promise.all([
        monitoringApi.getOverview(tenantId),
        monitoringApi.getMonitoredAssets(tenantId),
        monitoringApi.getAlerts({ tenant_id: tenantId, status: 'active' }),
      ]);
      setOverview(o.data);
      setAssets(a.data);
      setAlerts(al.data);
    } catch (err) { console.error('Failed to load monitoring data:', err); }
    finally { setLoading(false); }
  }, [tenantId]);

  useEffect(() => {
    if (isSentinelUser || tenant?.id) {
      loadData();
      const interval = setInterval(loadData, REFRESH_INTERVAL);
      return () => clearInterval(interval);
    }
  }, [loadData, isSentinelUser, tenant?.id]);

  async function handleCommand(assetId, commandType) {
    try {
      const params = {};
      if (commandType === 'restart_service') { const name = prompt('Service name to restart:'); if (!name) return; params.service_name = name; }
      if (commandType === 'collect_logs') { params.log_names = ['System', 'Application']; params.hours = 24; }
      await monitoringApi.queueCommand({ asset_id: assetId, command_type: commandType, parameters: params });
      setCommandSent(commandType);
      setTimeout(() => setCommandSent(null), 3000);
    } catch (err) { alert('Failed to send command: ' + (err.response?.data?.detail || err.message)); }
  }

  if (loading) {
    return (
      <div className="p-8"><div className="animate-pulse space-y-6">
        <div className="h-8 bg-gray-800 rounded w-64" />
        <div className="grid grid-cols-4 gap-4">{[1,2,3,4].map(i => <div key={i} className="h-32 bg-gray-800 rounded-xl" />)}</div>
      </div></div>
    );
  }

  if (selectedAsset) {
    const asset = assets.find(a => a.id === selectedAsset);
    if (asset) return <div className="p-8 max-w-[1400px]"><AssetDetail asset={asset} onBack={() => setSelectedAsset(null)} onCommand={handleCommand} isSentinelUser={isSentinelUser} /></div>;
  }

  return (
    <div className="p-8 max-w-[1400px]">
      <div className="flex items-center justify-between mb-8">
        <div><h1 className="text-2xl font-semibold text-white">Monitoring</h1><p className="text-sm text-gray-500 mt-1">Real-time endpoint health and telemetry</p></div>
        <div className="flex items-center gap-2 text-xs text-gray-500"><div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />Auto-refreshing every 30s</div>
      </div>
      {commandSent && (
        <div className="mb-4 px-4 py-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-sm text-emerald-400 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" /> Command "{commandSent}" queued successfully.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={Wifi} label="Online" value={overview?.online_assets || 0} color="emerald" sub={`${overview?.monitored_assets || 0} monitored`} />
        <StatCard icon={WifiOff} label="Offline" value={overview?.offline_assets || 0} color={overview?.offline_assets > 0 ? 'red' : 'gray'} />
        <StatCard icon={AlertTriangle} label="Active alerts" value={overview?.active_alerts || 0} color={overview?.critical_alerts > 0 ? 'red' : overview?.active_alerts > 0 ? 'amber' : 'gray'} sub={overview?.critical_alerts ? `${overview.critical_alerts} critical` : null} />
        <StatCard icon={Terminal} label="Pending commands" value={overview?.pending_commands || 0} color={overview?.pending_commands > 0 ? 'blue' : 'gray'} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-gray-800/40 border border-gray-700/50 rounded-xl p-5">
          <h3 className="text-sm font-medium text-gray-400 mb-4">Monitored endpoints</h3>
          {assets.length === 0 ? (
            <div className="text-sm text-gray-600 py-8 text-center">No agents reporting yet. Deploy an agent to start monitoring.</div>
          ) : (
            <div className="space-y-2">
              {assets.map((asset) => (
                <div key={asset.id} onClick={() => setSelectedAsset(asset.id)} className="flex items-center gap-4 p-3 rounded-lg hover:bg-gray-700/30 cursor-pointer transition-colors">
                  <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${asset.status === 'online' ? 'bg-emerald-400' : 'bg-red-400'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-200">{asset.name}</span>
                      {asset.active_alerts > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-400">{asset.active_alerts} alert{asset.active_alerts > 1 ? 's' : ''}</span>}
                    </div>
                    <div className="text-xs text-gray-500">{asset.hostname || asset.asset_type} · {asset.os_info || '--'}</div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <MiniGauge value={asset.telemetry?.cpu_percent} label="CPU" />
                    <MiniGauge value={asset.telemetry?.memory_percent} label="MEM" />
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-600 flex-shrink-0" />
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-gray-800/40 border border-gray-700/50 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4"><AlertTriangle className="w-4 h-4 text-gray-400" /><h3 className="text-sm font-medium text-gray-400">Active alerts</h3></div>
          {alerts.length === 0 ? (
            <div className="text-sm text-gray-600 py-4 text-center">No active alerts</div>
          ) : (
            <div className="space-y-2">
              {alerts.slice(0, 10).map((alert) => (
                <div key={alert.id} className="p-3 rounded-lg bg-gray-700/20">
                  <div className="flex items-start gap-2">
                    <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${alert.severity === 'critical' ? 'bg-red-400' : 'bg-amber-400'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-gray-200">{alert.title}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{alert.asset_name}</div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-600">
                        <span>{alert.occurrence_count}x</span>
                        <span>{alert.last_seen_at ? formatDistanceToNow(new Date(alert.last_seen_at), { addSuffix: true }) : ''}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
